/**
 * Race Page
 *
 * Host page for managing multiplayer math races.
 * Allows teachers to create race rooms, manage participants, and view results.
 */

import { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useRaceHost } from '@/hooks/useRaceHost';
import { useRaceSimulator } from '@/hooks/useRaceSimulator';
import {
  RaceConfigForm,
  RaceWaitingRoom,
  RaceCountdown,
  RaceLeaderboard,
  RaceResults,
} from '@/components/Race';
import styles from '@/components/Race/Race.module.css';

export function Race() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeRoomId = searchParams.get('resume');
  const hasAttemptedResume = useRef(false);

  const {
    phase,
    room,
    participants,
    leaderboard,
    countdown,
    error,
    isLoading,
    createRoom,
    resumeRoom,
    startRace,
    endRace,
    cancelRace,
  } = useRaceHost(user);

  const {
    simulatedCount,
    isAddingBots,
    addSimulatedParticipants,
    startSimulation,
    cleanupSimulation,
  } = useRaceSimulator({
    raceRoomId: room?.id || null,
    questionCount: room?.question_count || 20,
  });

  // Track previous phase to detect transition to 'racing'
  const prevPhaseRef = useRef(phase);

  // Resume room if resume param is provided
  useEffect(() => {
    if (resumeRoomId && user && !hasAttemptedResume.current && phase === 'configure') {
      hasAttemptedResume.current = true;
      console.log('Attempting to resume room:', resumeRoomId);
      resumeRoom(resumeRoomId);
    }
  }, [resumeRoomId, user, phase, resumeRoom]);

  // Sync URL with current room for refresh persistence
  useEffect(() => {
    if (room?.id && !resumeRoomId) {
      // Room exists but URL doesn't have resume param - add it
      navigate(`/race/host?resume=${room.id}`, { replace: true });
    }
  }, [room?.id, resumeRoomId, navigate]);

  // Start simulation when race begins
  useEffect(() => {
    if (prevPhaseRef.current !== 'racing' && phase === 'racing' && simulatedCount > 0) {
      console.log('Race started - starting bot simulation');
      startSimulation();
    }
    prevPhaseRef.current = phase;
  }, [phase, simulatedCount, startSimulation]);

  // Cleanup simulation when canceling and navigate back to dashboard
  const handleCancel = async () => {
    await cleanupSimulation();
    await cancelRace();
    navigate('/race');
  };

  if (!user) {
    return (
      <div className={styles.configContainer}>
        <h1 className={styles.pageTitle}>Race</h1>
        <p>Please sign in to host a race.</p>
      </div>
    );
  }

  return (
    <>
      {error && (
        <div className={styles.configContainer}>
          <div className={styles.errorMessage}>{error}</div>
        </div>
      )}

      {phase === 'configure' && (
        <RaceConfigForm onSubmit={createRoom} isLoading={isLoading} />
      )}

      {phase === 'waiting' && room && (
        <RaceWaitingRoom
          room={room}
          participants={participants}
          onStartRace={startRace}
          onCancel={handleCancel}
          simulatorProps={{
            simulatedCount,
            isAddingBots,
            addSimulatedParticipants,
            cleanupSimulation,
          }}
        />
      )}

      {phase === 'countdown' && countdown !== null && (
        <RaceCountdown count={countdown} />
      )}

      {phase === 'racing' && (
        <RaceLeaderboard
          room={room}
          leaderboard={leaderboard}
          onEndRace={endRace}
        />
      )}

      {phase === 'results' && (
        <RaceResults
          room={room}
          leaderboard={leaderboard}
        />
      )}
    </>
  );
}
